import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { CheckCircle, Package, Percent, Clock, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PreorderModal = ({ isOpen, onClose, product }) => {
  const navigate = useNavigate();
  
  if (!product) return null;
  
  const basePrice = product.pricing?.numericPrice || product.price || 0;
  const discountAmount = basePrice * 0.10; // 10% discount
  const preorderPrice = basePrice - discountAmount;
  const depositAmountNet = preorderPrice * 0.20; // 20% deposit (net)
  const depositAmountGross = depositAmountNet * 1.19; // Brutto inkl. 19% MwSt
  const remainingAmountNet = preorderPrice - depositAmountNet;
  const remainingAmountGross = remainingAmountNet * 1.19;
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const handleGoToCart = () => {
    onClose();
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center text-green-700">
            <CheckCircle className="mr-2 text-green-600" size={24} />
            Vorbestellung hinzugefügt
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Product Info */}
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            {product.image && (
              <img 
                src={product.image} 
                alt={product.name} 
                className="w-16 h-16 object-cover rounded"
              />
            )}
            <div>
              <h4 className="font-semibold text-gray-900">{product.name}</h4>
              <p className="text-sm text-amber-600 font-medium">Vorbestellung mit 10% Rabatt</p>
            </div>
          </div>
          
          {/* Price Breakdown */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Originalpreis:</span>
              <span className="text-gray-500 line-through">{formatPrice(basePrice)}</span>
            </div>
            <div className="flex justify-between text-sm text-green-700">
              <span className="flex items-center">
                <Percent size={14} className="mr-1" />
                Vorbestellrabatt (10%):
              </span>
              <span>-{formatPrice(discountAmount)}</span>
            </div>
            <div className="flex justify-between font-semibold border-t border-amber-300 pt-2">
              <span>Reduzierter Preis:</span>
              <span>{formatPrice(preorderPrice)}</span>
            </div>
          </div>
          
          {/* Deposit Info */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <CreditCard className="mr-2 text-red-600" size={18} />
              <span className="font-semibold text-red-800">Anzahlung (20%):</span>
            </div>
            <p className="text-2xl font-bold text-red-700">{formatPrice(depositAmountGross)}</p>
            <p className="text-xs text-gray-500">inkl. 19% MwSt ({formatPrice(depositAmountNet)} netto)</p>
            <p className="text-sm text-gray-600 mt-2">
              <Clock size={14} className="inline mr-1" />
              Restzahlung bei Lieferung: {formatPrice(remainingAmountGross)} inkl. MwSt
            </p>
          </div>
          
          {/* Info Text */}
          <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
            <p className="flex items-start">
              <Package className="mr-2 mt-0.5 text-amber-600 flex-shrink-0" size={16} />
              <span>
                Ihre Vorbestellung wird nach Eingang der Anzahlung reserviert. 
                Lieferzeit: ca. 60 Tage.
              </span>
            </p>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <Button 
              variant="outline" 
              onClick={handleContinueShopping}
              className="flex-1"
            >
              Weiter einkaufen
            </Button>
            <Button 
              onClick={handleGoToCart}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Zur Kasse
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PreorderModal;
